'use client'

import React, { Component } from 'react'

import Link from 'next/link'

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { DotLottieReact } from '@lottiefiles/dotlottie-react'

// Fallback UI component
const ErrorFallback = ({ errorMessage }: { errorMessage: string }) => {
  const lightImg = '/images/pages/misc-mask-1-light.png'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        position: 'relative',
        p: 3,
        overflow: 'hidden'
      }}
    >
      <Container
        maxWidth='sm'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 4,
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant='h3'
            component='h1'
            sx={{
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Terjadi kesalahan!
          </Typography>
          <Typography variant='h6' color='text.secondary'>
            Kami minta maaf, tetapi terjadi sesuatu yang tidak terduga.
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'error.main',
              p: 3,
              bgcolor: 'error.lighter',
              borderRadius: 2,
              textAlign: 'left',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              width: '100%'
            }}
          >
            <strong>Detail Kesalahan: </strong>
            {errorMessage}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '100%', height: '70dvh' }}>
          <DotLottieReact
            src='https://lottie.host/ba296723-8610-440f-813f-2d005e8914fe/09YLxUv5QF.lottie'
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </Box>

        <Button component={Link} href='/' variant='contained' size='large' sx={{ minWidth: 200 }}>
          Kembali ke Beranda
        </Button>
      </Container>

      <Box
        component='img'
        src={lightImg}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 0,
          display: { xs: 'none', md: 'block' }
        }}
      />
    </Box>
  )
}

// ErrorBoundary component
interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ errorMessage: string }>
}

interface ErrorBoundaryState {
  hasError: boolean
  errorMessage: string
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, errorMessage: '' }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error.message }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Error caught in ErrorBoundary:', error, info)

    // Opsional: Log ke service error tracking seperti Sentry
    // Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } })
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || ErrorFallback

      return <FallbackComponent errorMessage={this.state.errorMessage} />
    }

    return this.props.children
  }
}

export default ErrorBoundary
